import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";

const EMPTY = { name: "", role_fr: "", role_en: "", bio_fr: "", bio_en: "", photo: "", expertise: [], country: "", order: 0, active: true };
const inputCls = "w-full border border-tertiary bg-white px-3 py-2 text-sm outline-none focus:border-champagne focus:ring-1 focus:ring-champagne";
const labelCls = "mb-1 block text-xs uppercase tracking-[0.12em] text-anthracite/50";

export default function ExpertsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [expInput, setExpInput] = useState("");

  const load = () => api.get("/admin/experts").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = (item) => {
    if (item) { setEditing(item.id); setForm({ ...EMPTY, ...item }); setExpInput((item.expertise || []).join(", ")); }
    else { setEditing("new"); setForm(EMPTY); setExpInput(""); }
  };
  const close = () => setEditing(null);

  const save = async () => {
    const payload = { ...form, expertise: expInput.split(",").map((t) => t.trim()).filter(Boolean), order: Number(form.order) || 0 };
    if (editing === "new") await api.post("/admin/experts", payload);
    else await api.put(`/admin/experts/${editing}`, payload);
    toast.success("Enregistré"); close(); load();
  };
  const del = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/admin/experts/${id}`); load(); };

  return (
    <div className="max-w-4xl">
      <div className="mb-8 flex items-center justify-between">
        <div><h1 className="font-serif text-3xl text-anthracite">Experts</h1><p className="text-sm text-anthracite/50">L'expertise humaine de RSTI.</p></div>
        <button onClick={() => open(null)} data-testid="new-expert" className="inline-flex items-center gap-2 bg-anthracite px-5 py-2.5 text-sm font-medium text-ivory hover:bg-black"><Plus className="h-4 w-4" /> Ajouter</button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {items.length === 0 && <p className="text-sm text-anthracite/40">Aucun expert.</p>}
        {items.map((e) => (
          <div key={e.id} className="flex items-center gap-4 border border-tertiary bg-white p-4">
            {e.photo ? <img src={e.photo} alt={e.name} className="h-12 w-12 rounded-full object-cover" /> : <div className="flex h-12 w-12 items-center justify-center rounded-full bg-champagne/15 font-serif text-champagne">{e.name?.charAt(0)}</div>}
            <div className="flex-1"><p className="font-medium text-anthracite">{e.name}</p><p className="text-xs text-anthracite/50">{e.role_fr}</p></div>
            <button onClick={() => open(e)} className="rounded p-1.5 text-anthracite/50 hover:text-champagne"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => del(e.id)} className="rounded p-1.5 text-anthracite/50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-anthracite/40 px-4 py-6" data-testid="expert-modal">
          <div className="flex max-h-[90vh] w-full max-w-lg flex-col bg-white">
            <div className="shrink-0 px-6 pt-6 pb-4 flex items-center justify-between"><h2 className="font-serif text-xl text-anthracite">{editing === "new" ? "Nouvel expert" : "Modifier"}</h2><button onClick={close}><X className="h-5 w-5" /></button></div>
            <div className="min-h-0 flex-1 overflow-y-auto px-6" data-testid="expert-modal-body">
              <div className="space-y-3">
                <div><label className={labelCls}>Nom</label><input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="expert-name" className={inputCls} /></div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div><label className={labelCls}>Rôle (FR)</label><input value={form.role_fr} onChange={(e) => setForm((f) => ({ ...f, role_fr: e.target.value }))} className={inputCls} /></div>
                  <div><label className={labelCls}>Rôle (EN)</label><input value={form.role_en} onChange={(e) => setForm((f) => ({ ...f, role_en: e.target.value }))} className={inputCls} /></div>
                </div>
                <div><label className={labelCls}>Bio (FR)</label><textarea rows={2} value={form.bio_fr} onChange={(e) => setForm((f) => ({ ...f, bio_fr: e.target.value }))} className={inputCls} /></div>
                <div><label className={labelCls}>Bio (EN)</label><textarea rows={2} value={form.bio_en} onChange={(e) => setForm((f) => ({ ...f, bio_en: e.target.value }))} className={inputCls} /></div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <ImageUpload value={form.photo} onChange={(v) => setForm((f) => ({ ...f, photo: v }))} label="Photo" testid="expert-photo" />
                  <div><label className={labelCls}>Pays</label><input value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} className={inputCls} /></div>
                </div>
                <div><label className={labelCls}>Expertise (virgules)</label><input value={expInput} onChange={(e) => setExpInput(e.target.value)} className={inputCls} /></div>
              </div>
            </div>
            <div className="shrink-0 border-t border-tertiary px-6 py-4 flex justify-end gap-3">
              <button onClick={close} className="border border-tertiary px-5 py-2.5 text-sm hover:bg-sand">Annuler</button>
              <button onClick={save} data-testid="expert-save" className="bg-anthracite px-5 py-2.5 text-sm font-medium text-ivory hover:bg-black">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
